/*= ===========================================================================
  Free gift promotion

  Keeps the gift variants in window.freeGiftConfig in the cart (qty 1 each,
  tagged with the hidden `_free_gift` property) while the non-gift product
  subtotal is at or above the threshold, and removes them when it isn't.
  The price is zeroed by an automatic discount configured in Shopify admin.
============================================================================== */
(function () {
  const config = window.freeGiftConfig;
  if (!config || !config.variantIds || !config.variantIds.length) return;

  const GIFT_PROPERTY = '_free_gift';
  const CART_MUTATION = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/;
  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const nativeFetch = window.fetch.bind(window);

  let running = null;
  let queued = false;
  // Gifts that couldn't be added (e.g. sold out) aren't retried on this page
  const unavailable = new Set();

  const isGift = item => Boolean(item.properties && item.properties[GIFT_PROPERTY]);

  // Threshold is set in the shop currency; the cart is in the customer's presentment currency
  function thresholdInCartCurrency() {
    const rate = parseFloat(window.Shopify && window.Shopify.currency && window.Shopify.currency.rate) || 1;
    return Math.round(config.threshold * rate);
  }

  function qualifies(cart) {
    const subtotal = cart.items
      .filter(item => !isGift(item))
      .reduce((sum, item) => sum + item.final_line_price, 0);
    return subtotal >= thresholdInCartCurrency();
  }

  // Returns the line changes and additions needed to bring the cart in line with the promotion
  function plan(cart) {
    const eligible = qualifies(cart);
    const seen = {};
    const changes = [];

    cart.items.forEach(item => {
      if (!isGift(item)) return;
      const wanted = eligible && config.variantIds.includes(item.variant_id) && !seen[item.variant_id];
      seen[item.variant_id] = true;
      const quantity = wanted ? 1 : 0;
      if (item.quantity !== quantity) changes.push({ id: item.key, quantity });
    });

    const additions = eligible
      ? config.variantIds.filter(id => !seen[id] && !unavailable.has(id)).map(id => ({ id, quantity: 1, properties: { [GIFT_PROPERTY]: 'true' } }))
      : [];

    return { changes, additions };
  }

  function request(path, body) {
    return nativeFetch(`${root}${path}`, {
      method: body ? 'POST' : 'GET',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: body ? JSON.stringify(body) : undefined,
      cache: 'no-store'
    }).then(response => response.json().then(data => (response.ok ? data : Promise.reject(data))));
  }

  async function sync(forceRender) {
    const cart = await request('cart.js');
    const { changes, additions } = plan(cart);
    if (!changes.length && !additions.length && !forceRender) return;

    let updated = false;
    for (const change of changes) {
      await request('cart/change.js', change);
      updated = true;
    }
    // Add one at a time so a sold-out gift doesn't block the other
    for (const item of additions) {
      await request('cart/add.js', { items: [item] })
        .then(() => { updated = true; })
        .catch(() => unavailable.add(item.id));
    }

    if (document.body.classList.contains('cart')) {
      // The cart page is rendered server-side
      if (updated) window.location.reload();
    } else if (window.PXUTheme.refreshCart) {
      window.PXUTheme.refreshCart(await request('cart.js'));
    }
  }

  // Serialise syncs so overlapping cart updates can't double-add gifts
  function scheduleSync(forceRender) {
    if (running) {
      queued = true;
      return running;
    }
    running = sync(forceRender)
      .catch(() => {})
      .then(() => {
        running = null;
        if (queued) {
          queued = false;
          scheduleSync(true);
        }
      });
    return running;
  }

  // The theme's own cart requests go through jQuery
  if (window.jQuery) {
    window.jQuery(document).ajaxComplete((_event, _xhr, settings) => {
      if (CART_MUTATION.test(settings.url)) scheduleSync(false);
    });
  }

  // Catch cart updates made by apps using fetch
  window.fetch = function (input, init) {
    const result = nativeFetch(input, init);
    const url = typeof input === 'string' ? input : input && input.url;
    if (url && CART_MUTATION.test(url)) result.then(() => scheduleSync(false), () => {});
    return result;
  };

  // The theme can render a stale cart (e.g. after add-to-cart it re-fetches /cart.js before
  // the gifts are added), so re-sync and re-render whenever a rendered cart is out of step
  document.addEventListener('theme:cart:refreshed', event => {
    const { changes, additions } = plan(event.detail.cart);
    if (changes.length || additions.length) scheduleSync(true);
  });

  // Wait for the theme's cart code (app.js) to initialise
  document.addEventListener('DOMContentLoaded', () => scheduleSync(false));
})();
